import { useEffect, useRef, useSyncExternalStore } from 'react';
import * as scheduler from '../lib/tileLoadScheduler';

const INTERSECTION_ROOT_MARGIN = '200px';

/**
 * Gates a tile's mount behind the tile-load scheduler (see lib/tileLoadScheduler),
 * tracking this tile's own viewport visibility via IntersectionObserver.
 *
 * @param {{ id: string, tab?: string, isActiveTab: boolean, gridY?: number }} params
 * @returns {{ ref: import('react').RefObject, ready: boolean }}
 */
export function useTileLoadGate({ id, tab, isActiveTab, gridY }) {
  const ref = useRef(null);

  useEffect(() => {
    scheduler.registerTile(id, { tab, isActiveTab, gridY, isVisible: false });
    return () => scheduler.unregisterTile(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    scheduler.updateTileState(id, { isActiveTab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isActiveTab]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No IntersectionObserver support — degrade to "always load" rather
      // than leaving the tile stuck behind an observer that never fires.
      scheduler.updateTileState(id, { isVisible: true });
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => scheduler.updateTileState(id, { isVisible: entry.isIntersecting }),
      { rootMargin: INTERSECTION_ROOT_MARGIN, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const ready = useSyncExternalStore(
    (callback) => scheduler.subscribeReady(id, callback),
    () => scheduler.isReady(id),
  );

  return { ref, ready };
}

export default useTileLoadGate;
